export const Space = ' ' as const;
export const Ellipsis = '…' as const;
export const Empty = '' as const;

export function capitalize(string: string): string {
	if (string.length == 0) {
		return string;
	}
	return string[0].toUpperCase() + string.substring(1);
}

export function isEmptyOrWhitespace(input?: string | null): boolean {
	return !input || input.trim().length == 0;
}

export function cleanWhitespaces(input: string): string {
	return input.replace(/ +/g, Space);
}

export function reverse(input: string): string {
	return input.split('').reverse().join('');
}

export function ensureStartsWith(input: string, prefix: string): string {
	if (input.startsWith(prefix)) {
		return input;
	}
	return prefix + input;
}

export function ensureEndsWith(input: string, suffix: string): string {
	if (input.endsWith(suffix)) {
		return input;
	}
	return input + suffix;
}

export function normalizePhoneNumber(rawPhoneNumber: string): string {
	return ensureStartsWith(
		rawPhoneNumber
			.replace(/[\s/\\-]/g, '')
			.replace(/^0049/g, '')
			.replace(/^0/g, ''),
		'+49'
	);
}

export function shorten(string: string, maxCharCount: number): string {
	if (string.length <= maxCharCount) {
		return string;
	}
	return string.substring(0, maxCharCount - 1) + Ellipsis;
}

export function replace(input: string, target: string, replacement: string): string {
	return input.replace(new RegExp(escapeRegex(target), 'g'), replacement);
}

export function escapeRegex(regex: string): string {
	// Source: https://stackoverflow.com/questions/3446170/escape-string-for-use-in-javascript-regex
	return regex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function removePrefix(string: string, prefix: string): string {
	if (string.startsWith(prefix)) {
		return string.substring(prefix.length);
	}
	return string;
}

export function removeSuffix(string: string, suffix: string): string {
	if (string.endsWith(suffix)) {
		return string.substring(0, string.length - suffix.length);
	}
	return string;
}

export function removeHtmlTags(input: string): string {
	return input.replace(/<\/?[\w\d]+>/g, '');
}

export function afterLast(input: string, keyword: string) {
	const lastOffset = input.lastIndexOf(keyword);
	if (lastOffset == -1 || keyword == '') {
		return input;
	}
	return input.substring(lastOffset + keyword.length);
}

export function beforeFirst(string: string, separator: string) {
	const index = string.indexOf(separator);
	if (index === -1) {
		return string;
	}
	return string.substring(0, index);
}

export function startsWithAny(input: string, ...prefixes: string[]): boolean {
	return prefixes.some((suffix) => input.startsWith(suffix));
}

export function equalsAny<T = string | number | boolean>(input: T, ...candidates: T[]): boolean {
	return candidates.some((candidate) => candidate === input);
}

export function endsWithAny(input: string, ...suffices: string[]): boolean {
	return suffices.some((suffix) => input.endsWith(suffix));
}

export function includesAnyString(input: string, ...candidates: string[]): boolean {
	return candidates.some((candidate) => input.includes(candidate));
}

export function includesAll(input: string, ...candidates: string[]) {
	if (!candidates) {
		return true;
	}
	return candidates.every((candidate) => input.includes(candidate));
}

export function containsIgnoreCase(input: string, maybeSubstring: string): boolean {
	return input.toLowerCase().indexOf(maybeSubstring.toLowerCase().trim()) > -1;
}

export function compareStringsIgnoreCase(string1: string, string2: string): number {
	const uppercase1 = string1.toUpperCase();
	const uppercase2 = string2.toUpperCase();
	if (uppercase1 > uppercase2) {
		return 1;
	} else if (uppercase2 < uppercase1) {
		return -1;
	}
	return 0;
}

export function createHtmlBulletList(values: string[]): string {
	return (
		values.reduce((wholeText, item) => wholeText + '<li>• ' + item + '</li>', '<ul>') + '</ul>'
	);
}

export function truncateMiddle(path: string, maxLength: number) {
	if (path.length <= maxLength) {
		return path;
	}
	const halfMaxLength = Math.floor(maxLength / 2);
	return (
		path.substring(0, halfMaxLength - 1) + Ellipsis + path.substring(path.length - halfMaxLength)
	);
}

const localAtoB = (input: string) => {
	if (typeof Buffer === 'function') {
		// Node.JS / Bun
		return Buffer.from(input, 'base64').toString('binary');
	}
	// Browser
	return atob(input);
};

/**
 * Decodes the given base64 string to an UTF-8 string. This avoid artifacts that can appear when {@link window#atob()} is used directly.
 * Returns an empty string when the input is {@code null} or {@code undefined}.
 */
export function base64ToUtf8String(base64Input: string): string {
	if (!base64Input) {
		return Empty;
	}
	// Taken from https://stackoverflow.com/a/30106551/3989225
	// Going backwards: from bytestream, to percent-encoding, to original string.
	return decodeURIComponent(
		localAtoB(base64Input)
			.split('')
			.map((character) => '%' + ('00' + character.charCodeAt(0).toString(16)).slice(-2))
			.join(Empty)
	);
}

export function base64BinaryToString(base64Binary: string) {
	if (!base64Binary) {
		return Empty;
	}
	const binary = localAtoB(base64Binary);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < bytes.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return String.fromCharCode(...new Uint16Array(bytes.buffer));
}

/**
 * Returns the input until the last occurrence of the given separator.
 * If the separator is not found, returns the input string.
 */
export function partBeforeLast(input: string, separator: string): string {
	const lastIndex = input.lastIndexOf(separator);
	if (lastIndex < 0) {
		return input;
	}
	return input.substring(0, lastIndex);
}

export function getCommonPrefix(strings: string[]): string {
	if (strings.length === 0) {
		return '';
	}
	if (strings.length === 1) {
		return strings[0];
	}
	const sortedStrings = strings.sort();
	const firstString = sortedStrings[0];
	const lastString = sortedStrings[sortedStrings.length - 1];
	const minLength = Math.min(firstString.length, lastString.length);
	let i = 0;
	while (i < minLength && firstString.charAt(i) === lastString.charAt(i)) {
		i++;
	}
	return firstString.substring(0, i);
}

export function replaceAt(input: string, index: number, replacement: string) {
	return input.substring(0, index) + replacement + input.substring(index + replacement.length);
}

export function padLeft(input: string | number, targetLength: number): string {
	const inputLength = input.toString().length;
	if (inputLength >= targetLength) {
		return input.toString();
	}
	return '0'.repeat(targetLength - inputLength) + String(input);
}

export function countOccurrences(input: string, searchTerm: string) {
	let occurrences = 0;
	for (let i = 0; i < input.length - searchTerm.length + 1; i++) {
		if (input.substring(i, i + searchTerm.length) == searchTerm) {
			occurrences++;
		}
	}
	return occurrences;
}

const CHARS = [
	'A',
	'B',
	'C',
	'D',
	'E',
	'F',
	'G',
	'H',
	'I',
	'J',
	'K',
	'L',
	'M',
	'N',
	'O',
	'P',
	'Q',
	'R',
	'S',
	'T',
	'U',
	'V',
	'W',
	'X',
	'Y',
	'Z',
	'a',
	'b',
	'c',
	'd',
	'e',
	'f',
	'g',
	'h',
	'i',
	'j',
	'k',
	'l',
	'm',
	'n',
	'o',
	'p',
	'q',
	'r',
	's',
	't',
	'u',
	'v',
	'w',
	'x',
	'y',
	'z',
	'0',
	'1',
	'2',
	'3',
	'4',
	'5',
	'6',
	'7',
	'8',
	'9'
];

export function getRandomString(length: number) {
	let chars = '';
	for (let i = 0; i < length; ++i) {
		chars += randomChar();
	}
	return chars;
}

export function randomChar() {
	return CHARS[~~(Math.random() * CHARS.length)];
}

export function encodeForHtml(input: string) {
	return input
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#039;');
}

export function removeEnclosingQuotes(input: string) {
	for (const quote of ["'", '"', '`']) {
		if (input.startsWith(quote) && input.endsWith(quote)) {
			return removeSuffix(removePrefix(input, quote), quote);
		}
	}
	return input;
}
